'use client';

import { useState, useCallback, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useOfferedTemplates, templateTitle } from '@/contexts/templates/hooks/useOfferedTemplates';
import { useQueryClient } from '@tanstack/react-query';
import { Mic } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { VoiceCloneWizard } from '@/contexts/ai';
import type { CustomVoiceEntry } from '@/contexts/ai/types/voice';
import { useCurrentUserContext } from '@/contexts/authentication/client';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { storyKeys } from '@/contexts/stories';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Label } from '@/shared/components/ui/label';
import { CheckboxField } from '@/shared/components/ui/checkbox-field';
import Image from 'next/image';
import { cn } from '@/shared/utils/cn';
import { VoiceSelector, type Language } from '@/contexts/stories';
import type { VoiceSettings } from './VoiceStep';
import type { ExportOptions } from '../../services/wizardPayload';
import type { IllustrationFormat } from '@/contexts/illustrations';
import { staticUrl } from '@/shared/static';
import {
  COMIC_STYLES_BY_AGE,
  STYLE_PICKER_TILES,
  defaultComicStyleForAge,
  isComicStyle,
  type IllustrationStyleId,
} from '@/contexts/illustrations/services/styleTechnique';

// The ONE catalogue (styleTechnique.ts): order = parent appeal, and the hidden
// ones ('minimalist') are filtered there, not here. This array used to be its
// own list and drifted away from the campaign's and the portraits'. The comic
// sub-styles are one «Комикс» tile here, with the sub-style chips under the grid.
const BOOK_STYLES = STYLE_PICKER_TILES;

export type BookStyle = IllustrationStyleId;

// The vertical 2:3 page leads: it is the default for new books (bookFormat.DEFAULT_BOOK_FORMAT).
const BOOK_FORMATS: IllustrationFormat[] = ['portrait_2x3', 'print_landscape', 'print_portrait', 'square'];

const FORMAT_SIZES: Record<string, { w: number; h: number }> = {
  portrait_2x3: { w: 26, h: 39 },
  square: { w: 36, h: 36 },
  print_portrait: { w: 30, h: 38 },
  print_landscape: { w: 38, h: 30 },
};

export interface AudioOptions {
  autoGenerateBlocks: boolean;
  autoAssemble: boolean;
}

export interface IllustrationOptions {
  autoGeneratePortraits: boolean;
  autoGenerateImages: boolean;
}

interface OutputStepProps {
  wantAudio: boolean;
  wantIllustrations: boolean;
  onWantAudioChange: (v: boolean) => void;
  onWantBookChange: (v: boolean) => void;
  voiceSettings: VoiceSettings;
  onVoiceSettingsChange: (v: VoiceSettings) => void;
  illustrationStyle: BookStyle;
  onBookStyleChange: (v: BookStyle) => void;
  illustrationFormat: IllustrationFormat;
  onBookFormatChange: (v: IllustrationFormat) => void;
  audioOptions: AudioOptions;
  onAudioOptionsChange: (v: AudioOptions) => void;
  illustrationOptions: IllustrationOptions;
  onBookOptionsChange: (v: IllustrationOptions) => void;
  language?: string;
  /** The story's age group ('3-5', '7-10', …) — picks the default comic sub-style */
  ageGroup?: string;
  /** When true, the story checkbox is readonly (extend-existing mode) */
  isExtendMode?: boolean;
  /** Exports of the order (pipeline v2): PDF, print-ready file, video */
  exportOptions?: ExportOptions;
  onExportOptionsChange?: (v: ExportOptions) => void;
}

export function OutputStep({
  wantAudio,
  wantIllustrations,
  onWantAudioChange,
  onWantBookChange,
  voiceSettings,
  onVoiceSettingsChange,
  illustrationStyle,
  onBookStyleChange,
  illustrationFormat,
  onBookFormatChange,
  audioOptions,
  onAudioOptionsChange,
  illustrationOptions,
  onBookOptionsChange,
  language,
  ageGroup,
  isExtendMode,
  exportOptions,
  onExportOptionsChange,
}: OutputStepProps) {
  const t = useTranslations('content.wizard');
  const uiLocale = useLocale();
  // Style tiles come from the published illustration templates (admin →
  // Templates): their title and uploaded example; built-ins are the fallback.
  const offered = useOfferedTemplates();
  const tpl = (key: string) => offered?.illustrations.find((i) => i.key === key);
  const styleTitle = (key: string) => templateTitle(tpl(key)?.title, uiLocale) ?? t(`output.bookStyles.${key}`);
  const styleImage = (key: string) => tpl(key)?.exampleUrl ?? staticUrl(`/img/styles/${tpl(key)?.bundledPreview ?? key}.webp`);
  const bookStyleKeys: readonly string[] = offered?.illustrations.length
    ? offered.illustrations.filter((i) => i.kind !== 'comic' || i.key === 'comic').map((i) => i.key)
    : BOOK_STYLES;
  const queryClient = useQueryClient();
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const userCtx = useCurrentUserContext();
  const familyId = userCtx?.user?.familyId ?? null;

  // Track pending voice clone for SSE matching
  const pendingVoiceEntryIdRef = useRef<string | null>(null);

  // SSE: voice clone completed — update cache and auto-select
  useEventSourceEvent('voice.clone.completed', useCallback((payload: Record<string, unknown>) => {
    const voiceEntryId = payload.voiceEntryId as string;
    if (voiceEntryId !== pendingVoiceEntryIdRef.current) return;
    pendingVoiceEntryIdRef.current = null;

    const readyVoice: CustomVoiceEntry = {
      id: voiceEntryId,
      voiceId: (payload.voiceId as string) || voiceEntryId,
      name: (payload.name as string) || '',
      source: 'cloned',
      previewUrl: payload.previewUrl as string | undefined,
      addedBy: familyId || '',
      addedAt: new Date().toISOString(),
      status: 'ready',
    };

    if (familyId) {
      queryClient.setQueryData(
        storyKeys.customVoices(familyId),
        (old: unknown[] | undefined) => [readyVoice, ...(old ?? [])],
      );
      queryClient.invalidateQueries({ queryKey: storyKeys.customVoices(familyId) });
    }

    // Auto-select; the wizard stays open with the preview until the user closes it.
    onVoiceSettingsChange({ ...voiceSettings, voiceId: readyVoice.voiceId, voiceName: readyVoice.name });
  }, [familyId, queryClient, onVoiceSettingsChange, voiceSettings]));

  const needsSelection = isExtendMode && !wantAudio && !wantIllustrations;
  const needsVoice = wantAudio && !voiceSettings.voiceId;

  return (
    <div data-testid="content-wizard-output-step" className="space-y-4 w-full overflow-hidden">
      <div className="space-y-1">
        <h3 className="text-base font-medium">{t('output.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('output.description')}</p>
      </div>

      {/* Extend mode hint — must select at least one output */}
      {needsSelection && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
          <p className="text-sm text-warning-foreground dark:text-warning">{t('output.extendHint')}</p>
        </div>
      )}

      <div className="space-y-3">
        {/* Story — always checked */}
        <div className="rounded-md border px-4 py-3 bg-muted/30">
          <CheckboxField
            id="output-story"
            checked={true}
            disabled
            data-testid="output-story-checkbox"
            label={t('output.story')}
            hint={t('output.storyHint')}
          />
        </div>

        {/* Audio */}
        <div className="rounded-md border px-4 py-3 space-y-3">
          <CheckboxField
            id="output-audio"
            checked={wantAudio}
            onCheckedChange={(checked) => onWantAudioChange(checked)}
            data-testid="output-audio-checkbox"
            label={t('output.audio')}
            hint={t('output.audioHint')}
          />

          {/* Inline voice settings */}
          {wantAudio && (
            <div className="pl-7 space-y-3 " data-testid="output-audio-settings">
              <VoiceSelector
                selectedVoiceId={voiceSettings.voiceId}
                onVoiceSelect={(voiceId, voiceName) =>
                  onVoiceSettingsChange({ ...voiceSettings, voiceId, voiceName })
                }
                familyId={familyId ?? undefined}
                language={language as Language | undefined}
              />
              {needsVoice && (
                <p className="text-xs text-warning-foreground dark:text-warning">{t('output.voiceRequired')}</p>
              )}

              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={() => setShowCloneDialog(true)}
              >
                <Mic className="size-3.5" />
                {t('voice.cloneInline')}
              </Button>

              {familyId && (
                <VoiceCloneWizard
                  open={showCloneDialog}
                  onOpenChange={setShowCloneDialog}
                  familyId={familyId}
                  onEnqueued={(voiceEntryId) => {
                    pendingVoiceEntryIdRef.current = voiceEntryId;
                  }}
                />
              )}

              <div className="space-y-2 pt-2 border-t border-border/50">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="audio-auto-generate"
                    checked={audioOptions.autoGenerateBlocks}
                    onCheckedChange={(checked) => {
                      const generate = checked === true;
                      onAudioOptionsChange({
                        ...audioOptions,
                        autoGenerateBlocks: generate,
                        // Can't assemble without generated blocks
                        autoAssemble: generate ? audioOptions.autoAssemble : false,
                      });
                    }}
                    data-testid="audio-auto-generate-checkbox"
                  />
                  <Label htmlFor="audio-auto-generate" className="text-xs">{t('output.audioAutoGenerate')}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="audio-auto-assemble"
                    checked={audioOptions.autoAssemble}
                    disabled={!audioOptions.autoGenerateBlocks}
                    onCheckedChange={(checked) => onAudioOptionsChange({ ...audioOptions, autoAssemble: checked === true })}
                    data-testid="audio-auto-assemble-checkbox"
                  />
                  <Label htmlFor="audio-auto-assemble" className={cn('text-xs', !audioOptions.autoGenerateBlocks && 'text-muted-foreground')}>{t('output.audioAutoAssemble')}</Label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Book */}
        <div className="rounded-md border px-4 py-3 space-y-3">
          <CheckboxField
            id="output-book"
            checked={wantIllustrations}
            onCheckedChange={(checked) => onWantBookChange(checked)}
            data-testid="output-illustration-checkbox"
            label={t('output.book')}
            hint={t('output.bookHint')}
          />

          {/* Inline book style selector */}
          {wantIllustrations && (
            <div className="pl-7 " data-testid="output-illustration-settings">
              <Label className="text-xs">{t('output.bookStyle')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                {bookStyleKeys.map((style) => (
                  <button
                    key={style}
                    type="button"
                    onClick={() => {
                      // the comic tile opens the family at the age's default sub-style
                      if (style === 'comic') {
                        if (!isComicStyle(illustrationStyle)) onBookStyleChange(defaultComicStyleForAge(ageGroup));
                        return;
                      }
                      onBookStyleChange(style as BookStyle);
                    }}
                    className={cn(
                      'flex flex-col items-center gap-1 rounded-lg border-2 p-2 sm:p-2 transition-all hover:border-primary/50',
                      (style === 'comic' ? isComicStyle(illustrationStyle) : illustrationStyle === style)
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border'
                    )}
                    data-testid={`illustration-style-${style}`}
                  >
                    <div className="w-full aspect-square relative rounded-md overflow-hidden bg-muted">
                      <Image
                        src={styleImage(style)}
                        alt={styleTitle(style)}
                        fill
                        sizes="(max-width: 640px) 30vw, 120px"
                        className="object-cover"
                      />
                    </div>
                    <span className="text-[10px] sm:text-xs text-center">{styleTitle(style)}</span>
                  </button>
                ))}
              </div>

              {isComicStyle(illustrationStyle) && (
                <div className="mt-2" data-testid="comic-style-options">
                  <Label className="text-xs">{t('output.comicStyles.label')}</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {COMIC_STYLES_BY_AGE.map((sub) => (
                      <button
                        key={sub}
                        type="button"
                        onClick={() => onBookStyleChange(sub)}
                        className={cn(
                          'flex flex-col items-start rounded-lg border-2 px-2 py-1.5 text-left transition-all hover:border-primary/50',
                          illustrationStyle === sub ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border',
                        )}
                        data-testid={`illustration-style-${sub}`}
                        aria-pressed={illustrationStyle === sub}
                      >
                        <span className="text-xs font-medium">{t(`output.comicStyles.${sub}.name`)}</span>
                        <span className="text-[10px] text-muted-foreground">{t(`output.comicStyles.${sub}.hint`)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Label className="text-xs mt-3 block">{t('output.bookFormat')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {BOOK_FORMATS.map((format) => (
                  <button
                    key={format}
                    type="button"
                    onClick={() => onBookFormatChange(format)}
                    className={cn(
                      'flex flex-col items-center gap-1 rounded-lg border-2 p-2 transition-all hover:border-primary/50',
                      illustrationFormat === format
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border'
                    )}
                    data-testid={`illustration-format-${format}`}
                  >
                    <div
                      className="border border-muted-foreground/30 rounded-sm bg-muted"
                      style={{ width: FORMAT_SIZES[format].w, height: FORMAT_SIZES[format].h }}
                    />
                    <span className="text-[10px] text-center leading-tight">{t(`output.bookFormats.${format}`)}</span>
                  </button>
                ))}
              </div>

              <div className="space-y-2 pt-3 mt-3 border-t border-border/50">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="book-auto-portraits"
                    checked={illustrationOptions.autoGeneratePortraits}
                    onCheckedChange={(checked) => {
                      const portraits = checked === true;
                      onBookOptionsChange({
                        ...illustrationOptions,
                        autoGeneratePortraits: portraits,
                        // Can't generate pages without character portraits
                        autoGenerateImages: portraits ? illustrationOptions.autoGenerateImages : false,
                      });
                    }}
                    data-testid="illustration-auto-portraits-checkbox"
                  />
                  <Label htmlFor="book-auto-portraits" className="text-xs">{t('output.bookAutoPortraits')}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="book-auto-images"
                    checked={illustrationOptions.autoGenerateImages}
                    disabled={!illustrationOptions.autoGeneratePortraits}
                    onCheckedChange={(checked) => onBookOptionsChange({ ...illustrationOptions, autoGenerateImages: checked === true })}
                    data-testid="illustration-auto-images-checkbox"
                  />
                  <Label htmlFor="book-auto-images" className={cn('text-xs', !illustrationOptions.autoGeneratePortraits && 'text-muted-foreground')}>{t('output.bookAutoImages')}</Label>
                </div>
              </div>
            </div>
          )}
        </div>

        {wantIllustrations && exportOptions && onExportOptionsChange && (
          <ExportFields value={exportOptions} onChange={onExportOptionsChange} videoAvailable={wantAudio} />
        )}

        {/* Public-publishing consent moved to the REVIEW (last) step — see
            ReviewStep + PublicConsentField. */}
      </div>
    </div>
  );
}

/**
 * Exports of the order (pipeline v2 «Выгрузки»): each starts by itself once its
 * inputs are ready. PDF comes with every book; the print-ready file and the
 * video are asked for. The YouTube audio publication stays in publishing.
 */
export function ExportFields({ value, onChange, videoAvailable }: {
  value: ExportOptions;
  onChange: (v: ExportOptions) => void;
  /** the video needs the narration */
  videoAvailable: boolean;
}) {
  const t = useTranslations('content.wizard.output.exports');
  const row = (key: keyof ExportOptions, disabled = false) => (
    <div className="flex items-center gap-2" key={key}>
      <Checkbox
        id={`export-${key}`}
        checked={value[key] && !disabled}
        disabled={disabled}
        onCheckedChange={(checked) => onChange({ ...value, [key]: checked === true })}
        data-testid={`export-${key}-checkbox`}
      />
      <Label htmlFor={`export-${key}`} className={cn('text-xs', disabled && 'text-muted-foreground')}>{t(key)}</Label>
    </div>
  );
  return (
    <div className="space-y-2 rounded-lg border border-border/60 p-3" data-testid="export-fields">
      <Label className="text-xs font-medium">{t('title')}</Label>
      {row('pdf')}
      {row('print')}
      {row('video', !videoAvailable)}
      {!videoAvailable && <p className="text-[10px] text-muted-foreground" data-testid="export-video-hint">{t('videoNeedsAudio')}</p>}
    </div>
  );
}
